import { Link } from "react-router";
import { ArrowLeft } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { useAuth } from "@/lib/auth-context";
import { useNotificationRules } from "@/features/settings/use-notification-rules";
import { useSegments } from "@/features/settings/use-segments";
import { NotificationRulesTable } from "./_components/notification-rules-table";
import { InsightsBar } from "./_components/insights-bar";

// Phase 7 sub-bucket 18 — SPA port of
// /app/admin/settings/notifications/page.tsx. Per-row inline edit; the
// "Test send" button fires useTestSendNotification with the row's event key.
//
// Sub-bucket 19 — adds the 30-day analytics InsightsBar above the rules
// table. Backed by the new GET /api/settings/notifications/insights which
// surfaces `loadNotificationInsights` (deliveries-by-channel aggregate).
// The four notification-rule mutations flush `settings.notificationInsights()`
// so the bar refreshes on every rule change.

export function NotificationsPage() {
  const session = useAuth().session;
  const canManage = !!session?.permissions.includes("notifications.write");
  const canRead = !!session?.permissions.includes("notifications.read");

  const rulesQuery = useNotificationRules();
  const segmentsQuery = useSegments();
  const partnerSegments = (segmentsQuery.data ?? []).filter(
    (s) => s.entity_type === "partner",
  );

  if (!canRead) {
    return (
      <div className="py-8">
        <header className="mb-7">
          <h1 className="font-display text-3xl font-semibold tracking-tight">
            Notifications
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            You don&apos;t have permission to view notification rules.
          </p>
        </header>
      </div>
    );
  }

  return (
    <div>
      <Link
        to="/admin/settings"
        className="mb-4 inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
      >
        <ArrowLeft className="h-3 w-3" />
        Back to settings
      </Link>

      <header className="mb-7">
        <h1 className="font-display text-3xl font-semibold tracking-tight">
          Notifications
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
          One rule per event. Toggle channels, scope to a segment, and the
          dispatcher fans out — mail through the Gmail API, chat through Slack.
        </p>
      </header>

      <InsightsBar />

      {rulesQuery.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-32 w-full rounded-xl" />
          ))}
        </div>
      ) : (
        <NotificationRulesTable
          rules={rulesQuery.data ?? []}
          segments={partnerSegments}
          canManage={canManage}
        />
      )}
    </div>
  );
}
